import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {TranslatePipe} from "@ngx-translate/core";
import {UpperCasePipe} from "@angular/common";
import {NgOptimizedImage} from "@angular/common";
import {NotificationService} from "../../../services/notification.service";
import {getPathWithoutQueryAndHash} from "../../../shared/utils/url.utils";
import {NavigateToMessagesService} from "../../../services/navigate-to-messages.service";
import {AppMessage} from "../../../models/app-message.model";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {PlatformService} from "../../../services/platform.service";
import {UserInfoService} from "../../../services/user-info.service";
import {filterMessagesForContext} from "../../../shared/utils/message-visibility.utils";
import {NavItemPlatform} from "../../../models/nav-item-platform.model";

export type BottomNavItemType = "messages" | "link";

export interface BottomNavItem {
  type: BottomNavItemType;
  labelKey: string;
  icon: string;
  iconAlt?: string;
  routerLink?: string | string[];
  routerLinkExact?: boolean;
  ariaLabelKey: string;
  ariaLabelKeyNew?: string;
  platform: NavItemPlatform;
}

@Component({
  selector: "app-bottom-nav",
  templateUrl: "./bottom-nav.component.html",
  styleUrls: ["./bottom-nav.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    UpperCasePipe,
    NgOptimizedImage,
  ],
})
export class BottomNavComponent implements OnInit, OnDestroy {
  areNewMessages = false;
  messagesList: AppMessage[] = [];
  navItems: BottomNavItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private navigateToMessages: NavigateToMessagesService,
    public router: Router,
    private platformService: PlatformService,
    private userInfoService: UserInfoService,
  ) {
    this.navItems = this.buildNavItems();
  }

  ngOnInit(): void {
    this.subscribeToNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildNavItems(): BottomNavItem[] {
    return [
      {
        type: "link",
        labelKey: "sideBottomNav.services",
        icon: "assets/header-icons/Services.svg",
        ariaLabelKey: "sideBottomNav.services",
        routerLinkExact: false,
        platform: "always",
      },
      {
        type: "messages",
        labelKey: "sideBottomNav.messages",
        icon: "assets/header-icons/Messages.svg",
        iconAlt: "assets/header-icons/Messages-dot.svg",
        ariaLabelKey: "accessibility.goMessagePage",
        ariaLabelKeyNew: "accessibility.goMessagePageNewMessage",
        platform: "always"
      },
      {
        type: "link",
        labelKey: "sideBottomNav.myHealth",
        icon: "assets/header-icons/MyHealth.svg",
        routerLink: "/my-health",
        routerLinkExact: false,
        ariaLabelKey: "sideBottomNav.myHealth",
        platform: "epak"
      },
      {
        type: "link",
        labelKey: "sideBottomNav.videovisits",
        icon: "assets/header-icons/VideoVisits.svg",
        routerLink: "/video",
        routerLinkExact: false,
        ariaLabelKey: "sideBottomNav.videovisits",
        platform: "paid"
      },
      {
        type: "link",
        labelKey: "sideBottomNav.more",
        icon: "assets/header-icons/More.svg",
        routerLink: "/more",
        routerLinkExact: true,
        ariaLabelKey: "sideBottomNav.more",
        platform: "always"
      },
    ];
  }

  get visibleNavItems(): BottomNavItem[] {
    const brandName = this.platformService.brandName();
    const isPaid = brandName === this.platformService.paidPlatform;
    const isEpak = brandName === this.platformService.epakPlatform;
    const isExternal = this.userInfoService.getIsExternalPatient();

    return this.navItems
      .map((item) => {
        if (isExternal && item.labelKey === "sideBottomNav.myHealth") {
          return {
            ...item,
            labelKey: "sideBottomNav.myAccount",
            icon: "assets/header-icons/Account.svg",
            routerLink: "/user-profile",
            ariaLabelKey: "sideBottomNav.myAccount",
            platform: "always" as NavItemPlatform,
          };
        }
        return item;
      })
      .filter((item) => {
        if (isExternal && item.labelKey === "sideBottomNav.connectedDevices") {
          return false;
        }
        if (item.platform === "always") return true;
        if (item.platform === "paid") return isPaid;
        if (item.platform === "epak") return isEpak;
        return false;
      });
  }

  getNavItemRouterLink(item: BottomNavItem): string[] {
    if (item.type === "link" && item.routerLink) {
      return Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
    }
    if (item.labelKey === "sideBottomNav.services") {
      return this.isPaidPlatform ? ["/paid"] : ["/"];
    }
    return [];
  }

  isNavItemActive(item: BottomNavItem): boolean {
    if (item.type === "messages") {
      return this.router.url.includes("/message");
    }
    if (item.labelKey === "sideBottomNav.services") {
      return this.isServicesActive;
    }
    return false;
  }

  private subscribeToNotifications(): void {
    this.notificationService
      .getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe((messages: AppMessage[]) => {
        this.messagesList = messages;
        const visibleMessages = filterMessagesForContext(messages, {
          isPaidPlatform: this.platformService.brandName() === this.platformService.paidPlatform,
          isExternalPatient: this.userInfoService.getIsExternalPatient(),
        });
        this.areNewMessages = visibleMessages.some(
          (m) => m.isUnread && m.directionToPatient
        );
      });
  }

  onMessagesClick(event: Event): void {
    event.preventDefault();
    this.navigateToMessages.navigateToMessages();
  }

  get isPaidPlatform(): boolean {
    return this.platformService.brandName() === this.platformService.paidPlatform;
  }

  get isServicesActive(): boolean {
    if (this.router.url.includes("/message")) return false;
    const path = getPathWithoutQueryAndHash(this.router.url);
    if (this.isPaidPlatform) {
      return path === "/paid" || path.startsWith("/paid/");
    }
    return path === "/";
  }
}
